/**
 * Singleton toast notification queue.
 *
 * One queue across the whole app. `<ToastContainer />` is mounted once at
 * App.vue and renders the active toasts. Stores / views call
 * `useToast().success("Saved")` (or .error / .info) and the toast
 * auto-dismisses after `DEFAULT_MS` unless `sticky` is set.
 */
import { ref } from "vue";

export type ToastKind = "success" | "error" | "info";

export interface ToastAction {
  label: string;
  /** Called when the user clicks the action button — a direct user
   *  gesture, so `window.location.href` and similar otherwise-blocked
   *  operations work safely inside it. */
  onClick: () => void;
}

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  detail?: string;
  /** Optional CTA rendered as a button inside the toast. */
  action?: ToastAction;
  /** If true, won't auto-dismiss; user must click ×. */
  sticky?: boolean;
  /** ms before auto-dismiss. Ignored when sticky. */
  ms?: number;
}

const DEFAULT_MS = 4000;
const ERROR_MS = 6000; // errors deserve a beat longer

const toasts = ref<Toast[]>([]);
let nextId = 1;

function show(input: Omit<Toast, "id">): number {
  const id = nextId++;
  const toast: Toast = { id, ...input };
  toasts.value = [...toasts.value, toast];
  if (!input.sticky) {
    const ttl = input.ms ?? (input.kind === "error" ? ERROR_MS : DEFAULT_MS);
    setTimeout(() => dismiss(id), ttl);
  }
  return id;
}

function dismiss(id: number) {
  toasts.value = toasts.value.filter((t) => t.id !== id);
}

function clear() {
  toasts.value = [];
}

export interface ToastOptions {
  action?: ToastAction;
  sticky?: boolean;
  ms?: number;
}

export function useToast() {
  return {
    toasts,
    show,
    dismiss,
    clear,
    success(message: string, detail?: string, opts?: ToastOptions) {
      return show({ kind: "success", message, detail, ...opts });
    },
    error(message: string, detail?: string, opts?: ToastOptions) {
      return show({ kind: "error", message, detail, ...opts });
    },
    info(message: string, detail?: string, opts?: ToastOptions) {
      return show({ kind: "info", message, detail, ...opts });
    },
  };
}
